import { Component, Ref, Vue } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { ValidationObserver } from 'vee-validate';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import HttpStatus from 'http-status-codes';
import remove from 'lodash/remove';

import Constants from '@/constants';
import { EVENT_ID } from '@/constants/event-id';
import { ORDER_STATE_ID } from '@/constants/order-state-id';
import { FEE_TYPE } from '@/constants/fee-type';
import { i18n } from '@/core/i18n';
import { Currency } from '@/core/currency';
import { initializeDatadog, setDatadogCorrelationId } from '@/core/datadog/datadog-rum';
import './validations';

import { CheckoutService } from '@/services';
import { formatCurrency, formatCurrencyRounded } from '@/core/currency/helpers';
import { AppSkeleton } from '@/views/app/components';
import { ChargeDetails, TokenData } from '@/views/checkout/components/charge-dialog/interfaces';
import { PAYMENT_METHOD_ID, PaymentMethod } from '@/core/payment-methods/constants';
import { CREDIT_CARD } from '@/core/payment-methods/constants/methods';
import { Tracker } from '@/views/checkout/utilities';
import { DEFAULT_PAYMENT_METHOD_LIST_ORDER, PAYMENT_METHOD_WHITELIST } from '@/constants/payment-methods';
import { PAYMENT_METHOD_LIST_ORDER_BY_COUNTRY } from '@/constants/payment-methods-country-order';
import { getPaymentMethodComponent } from '@/views/checkout/components/payment-methods/dynamic-components';
import {
    filterPaymentMethods,
    findPaymentMethod,
    hasPaymentMethod,
    orderPaymentMethods,
    unsupportedBrowserPaymentMethods
} from '@/core/payment-methods/utilities';
import { DIGITAL_PAYMENT_METHOD_GROUP } from '@/core/payment-methods/constants/groups';
import { OtpStatus } from '@/core/otp/constants';
import Account from '@/views/checkout/mixins/account';
import { AVAILABLE_FEE_CONFIGS } from '@/constants/fee';
import {
    filterPaymentMethodFees,
    getFeesByUrl,
    getPaymentMethodFees,
    getTotalFeeAmount
} from '@/views/checkout/utilities/fee';
import { getOrderStateEventId, isOrderClosed } from '@/views/checkout/utilities/order-status';
import { AccountService } from '@/services/checkout';
import { EventId } from '@/core/otp/services';
import { LoyaltySuccessModal } from '@/core/payment-card/components';
import { SurchargeInfo, TransactionInfo } from '@/core/payment-card/interfaces';
import {
    LoyaltyProgramId
} from '@/views/checkout/components/payment-methods/payment-card/constants/loyalty-program-id';
import {
    getForcedRedirectPaymentMethodId,
    getPaymentMethodRedirectUrl
} from '@/views/checkout/utilities/payment-methods';
import { redirectLogin } from '@/views/checkout/utilities/user';
import {
    hasSurcharge,
    updateSurcharge,
    supportsSurcharge,
    getSurchargeAmount
} from '@/views/checkout/utilities/surcharge';
import { setParamsOnBrowserHistory } from '@/views/checkout/utilities/browser-history';
import { setDatadogOrderCode } from '@/views/checkout/utilities/datadog';
import { Config, Customer, Fee, GiftCardConfig, Merchant, Order, User } from './interfaces';
import {
    ChargeDialog,
    DeclineModal,
    DiscountDetails,
    ErrorModal,
    ExchangeDetails,
    FeeDetails,
    FeeInfo,
    MerchantDetails,
    NotificationBar,
    PaymentDescription,
    PaymentDetails,
    PaymentMethodSlideGroup,
    TermsAndConditions
} from './components';
import { convertAmountToBaseUnits, convertAmountToCents } from "@/core/helpers/amount";
import { SERVICE_ID } from "@/constants/service-id";

Currency.Filters.useFormatCurrencyFilter();

@Component({
    data() {
        return {
            amount: null,
            tokenData: {
                chargeToken: '',
                redirectToACSForm: ''
            },
            chargeDetails: null,
            order: null,
            merchant: null,
            giftCardConfig: null,
            user: null,
            config: null,
            customer: null,
            loading: false,
            userInputAmount: null,
            languages: Constants.i18n.LANGUAGES,
            chargeModalOpen: false,
            selectedCarouselMethod: null,
            selectedMethod: null,
            eventId: null,
            isSnackbarShown: false,
            isDeclineModalShown: false,
            errorModalShown: false,
            otp: {
                enabled: false,
                limitReached: false,
                requestTimestamp: 0,
                status: OtpStatus.None
            },
            processing: false,
            loyaltyRedeemedAmount: null,
            hasRedemption: false,
            loyaltySuccessModalOpened: false,
            transactionInfo: null,
            rfPaymentCode: '',
            errorModalMessage: '',
            customerNotes: ''
        };
    },
    components: {
        FeeInfo,
        FeeDetails,
        AppSkeleton,
        ChargeDialog,
        ErrorModal,
        PaymentDetails,
        LoyaltySuccessModal,
        DiscountDetails,
        ExchangeDetails,
        MerchantDetails,
        NotificationBar,
        DeclineModal,
        ValidationObserver,
        PaymentDescription,
        TermsAndConditions,
        PaymentMethodSlideGroup
    }
})
export default class Checkout extends mixins(
    i18n.Mixins.Culture,
    Currency.Mixins.Currency,
    Account
) {
    @Ref() readonly amountForm!: InstanceType<typeof ValidationObserver>;
    @Ref() readonly notesForm!: InstanceType<typeof ValidationObserver>;

    order: Order;
    merchant: Merchant;
    giftCardConfig: GiftCardConfig;
    config: Config;
    customer: Customer;
    user: User | null;
    loading: boolean;
    userInputAmount: string;
    amount: number;
    chargeModalOpen: boolean;
    tokenData: TokenData;
    chargeDetails: ChargeDetails;
    selectedCarouselMethod: PaymentMethod | null;
    selectedMethod: PaymentMethod | null;
    eventId: number | null;
    isSnackbarShown: boolean;
    isDeclineModalShown: boolean;
    errorModalShown: boolean;
    otp: any;
    processing: boolean;
    loyaltySuccessModalOpened: boolean;
    transactionInfo: TransactionInfo;
    rfPaymentCode: string;
    errorModalMessage: string;
    customerNotes: string;

    private availableFees: Array<Fee> = [];

    async created() {
        this.loading = true;
    }

    async mounted() {
        if (!this.$route.query.ref) {
            this.loading = false;

            return;
        }

        const ref = (this.$route.query.ref as string) || '';
        const fees = getFeesByUrl(AVAILABLE_FEE_CONFIGS, this.$route.query);

        const response = await CheckoutService.getOrder(ref, fees);

        if (!response.success) {
            this.loading = false;

            return;
        }

        await initializeDatadog();
        setDatadogCorrelationId(response.correlationId);

        /*
         * Apply get order response
         */
        this.order = response.data.order;
        this.merchant = response.data.merchant;
        this.config = response.data.config;
        this.customer = response.data.customer;
        this.config.idempotencyId = uuidv4();
        this.user = response.data.user;
        this.giftCardConfig = response.data.giftCardConfig;

        if (this.order?.orderCode) {
            setDatadogOrderCode(this.order.orderCode);
        }

        if (this.order?.currencyCode) {
            this.currency = this.getCurrencyByCode(this.order.currencyCode);
        }

        if (this.merchant.paymentMethods.includes(PAYMENT_METHOD_ID.Dias)) {
            const paymentSessionResponse = await CheckoutService.getPaymentSessions({
                orderCode: this.order.orderCode
            });

            if (paymentSessionResponse.success && paymentSessionResponse.data.rfPaymentCode) {
                this.rfPaymentCode = paymentSessionResponse.data.rfPaymentCode;
            }
        }

        if (await this.forceRedirect()) {
            return;
        }

        // Apply language
        if (!this.getStoredLang()) {
            const requestLang = this.sanitizeLang(this.order.requestLang);

            if (requestLang) {
                await this.setLang(requestLang);
            } else if (this.countryCode) {
                await this.setCountryLang(this.countryCode);
            }
        }

        // Redirect to status page, when order state is paid, canceled or expired
        if (isOrderClosed(this.order.stateId)) {
            if (this.order.stateId === ORDER_STATE_ID.Paid && this.order.urlSuccess) {
                window.location.href = this.order.urlSuccess;

                return;
            }

            if (this.order.stateId === ORDER_STATE_ID.Expired) {
                window.location.href = this.order.urlFail;

                return;
            }

            await this.redirectToStatusPage(
                +this.order.stateId === ORDER_STATE_ID.Paid,
                getOrderStateEventId(+this.order.stateId)
            );

            return;
        }

        // Fees from url params are allowed only for orders with non-editable amount
        if (this.order?.paymentMethodFees?.length > 0) {
            this.availableFees = filterPaymentMethodFees(AVAILABLE_FEE_CONFIGS, this.order?.paymentMethodFees);
        } else if (!this.order.editableAmount) {
            this.availableFees = fees;
        }

        // Select payment method
        this.selectedCarouselMethod = this.defaultMethod;

        // Redirect to fail status page, when merchant has no payment method
        if (!this.merchant.paymentMethods.length) {
            await this.redirectToStatusPage(false, EVENT_ID.ThirdPartyCheckoutError);

            return;
        }

        if (!this.order?.currencyCode && this.merchant?.countryCode) {
            this.setCurrency(this.merchant.countryCode);
        }

        this.$emit('channel-loaded', this.order.channelId || '');

        this.loading = false;

        this.populateDataFromUrlParams();

        this.$nextTick(() => {
            Tracker.trackCheckoutInitiated(this.customer, this.order, this.merchant, this.currency);
        });
    }

    get chargeAmount() {
        const surchargeSupported = supportsSurcharge(this.selectedCarouselMethod);

        return hasSurcharge(this.availableFees) && surchargeSupported
            ? this.amount + convertAmountToBaseUnits(getSurchargeAmount(this.availableFees))
            : this.amount;
    }

    get countryCode() {
        return this.customer?.countryCode || this.merchant?.countryCode;
    }

    get defaultMethod() {
        if (this.carouselPaymentMethods.length > 0) {
            return this.carouselPaymentMethods[0];
        }

        return null;
    }

    get htmlUnescapedNotes() {
        if (this.order.notes) {
            return this.unescapeHtml(this.order.notes);
        }

        return '';
    }

    //We need to decouple customer notes from order notes because if fill them
    //we would break this condition, used to detect arrival from quick pay
    get editableNotes() {
        return (!this.order.notes && this.order.editableAmount) || this.requiresPaymentCode;
    }

    /*
     * Synchronous mirror of the `required` rule of the customer notes textarea
     * (payment-description.vue), so wallets can block before opening a native sheet
     */
    get notesMissing() {
        return this.editableNotes && !this.htmlUnescapedNotes && !this.customerNotes.trim();
    }

    unescapeHtml(html: string) {
        const notes = new DOMParser().parseFromString(html, 'text/html');

        return notes.documentElement.textContent;
    }

    async forceRedirect() {
        if (this.order.editableAmount ||
            !this.$route.query.forceredirect
        ) {
            return false;
        }

        const forcedRedirectPaymentMethodId = getForcedRedirectPaymentMethodId(this.availablePaymentMethods);

        if (!forcedRedirectPaymentMethodId) {
            return false;
        }

        if (this.hasForcedRedirectCancelled(forcedRedirectPaymentMethodId)) {
            if (!this.order.urlCancel) {
                window.location.href = this.order.urlFail;

                return true;
            }

            const { success, data } = await CheckoutService.cancelPaymentOrder(+this.order.orderCode);

            if (!success) {
                this.error(null);

                return false;
            }

            Tracker.trackPaymentCancelled();

            window.location.href = this.order.urlCancel;

            return true;
        }

        if (forcedRedirectPaymentMethodId === PAYMENT_METHOD_ID.Wallet) {
            /**
             * Ignore force redirect for logged in user,
             * to proceed with OTP step through Smart Checkout
             */
            if (this.user) {
                return false;
            }

            Tracker.trackCheckoutInitiated(this.customer, this.order, this.merchant, this.currency, 'redirect_initiated');

            this.setParamsOnForceRedirect({ cancelorder: 'true' });

            redirectLogin(forcedRedirectPaymentMethodId, this.getLang());

            return true;
        }

        Tracker.trackCheckoutInitiated(this.customer, this.order, this.merchant, this.currency, 'redirect_initiated');

        const redirectUrl = await getPaymentMethodRedirectUrl(
            this.order,
            this.customer,
            forcedRedirectPaymentMethodId
        );

        if (!redirectUrl) {
            return false;
        }

        this.setParamsOnForceRedirect({ cancelorder: 'true' });

        window.location.href = redirectUrl;

        return true;
    }

    hasForcedRedirectCancelled(id: PAYMENT_METHOD_ID) {
        if (id === PAYMENT_METHOD_ID.Wallet && this.user) {
            return false;
        }

        const storedParameters = JSON.parse(Vue.$sessionStorage.getItem('order-url-parameters') || '{}');

        return this.$route.query.ref !== storedParameters.ref ||
            !!this.$route.query.cancelorder;
    }

    setParamsOnForceRedirect(params:object) {
        Vue.$sessionStorage.setItem('order-url-parameters', JSON.stringify({
            ...this.$route.query,
            ...params
        }));

        setParamsOnBrowserHistory(params);
    }

    validateRequestAmount(requestAmount: string) {
        return (
            this.order.editableAmount &&
            requestAmount &&
            /^\d+$/.test(requestAmount) &&
            !this.order.notes
        );
    }

    populateDataFromUrlParams() {
        const requestAmount = this.$route.query.requestamount as string;

        if (this.validateRequestAmount(requestAmount)) {
            this.amount = convertAmountToBaseUnits(parseInt(requestAmount));

            const { notes } = this.$route.query;

            if (notes) {
                this.order.notes = notes as string;
            }
        } else {
            this.amount = convertAmountToBaseUnits(this.order?.amount) || 0;
        }

        this.userInputAmount = this.amount.toString();

        const paymentMethod = this.$route.query.paymentmethod as string;

        if (paymentMethod) {
            const paymentMethodId = +paymentMethod;

            if (hasPaymentMethod(this.availablePaymentMethods, paymentMethodId)) {
                this.selectedCarouselMethod = findPaymentMethod(paymentMethodId) as PaymentMethod;
            }
        }
    }

    /*
     * Get available payment methods
     * based on application whitelisted payment methods,
     * merchant's assigned payment methods
     * and methods supported by the browser
     */
    get availablePaymentMethods(): Array<PaymentMethod> {
        // Merchant payment methods available on smart checkout application
        const merchantMethods = filterPaymentMethods(PAYMENT_METHOD_WHITELIST, this.merchant?.paymentMethods);

        // Exclude browser unsupported payment methods
        const supportedMerchantMethods = filterPaymentMethods(
            merchantMethods,
            unsupportedBrowserPaymentMethods().map((method) => method.id),
            true
        );

        const countryCode = this.customer?.countryCode || this.merchant?.countryCode;

        const paymentListOrder = PAYMENT_METHOD_LIST_ORDER_BY_COUNTRY[countryCode] ?? DEFAULT_PAYMENT_METHOD_LIST_ORDER;

        return orderPaymentMethods(supportedMerchantMethods, paymentListOrder);
    }

    /*
     * Get available digital payment methods,
     * filter by application whitelisted, merchant's assigned and browser supported
     */
    get availableDigitalPaymentMethods(): Array<PaymentMethod> {
        return filterPaymentMethods(
            this.availablePaymentMethods,
            DIGITAL_PAYMENT_METHOD_GROUP.map((method) => method.id)
        );
    }

    /*
     * Digital payment methods are applied also as express payment methods,
     * when there are no fees or when tbi fee applied
     */
    get expressPaymentMethods(): Array<PaymentMethod> {
        if (this.availableFees?.length > 0 &&
            !this.order?.paymentMethodFees?.length &&
            !this.availableFees.some((item) => item.type === FEE_TYPE.TbiBankFee)) {
            return [];
        }

        return this.availableDigitalPaymentMethods ?? [];
    }

    get carouselEnabled(): boolean {
        return this.carouselPaymentMethods?.length > 0 &&
            !(this.carouselPaymentMethods.length === 1 &&
                this.carouselPaymentMethods.some((method) => method.id === CREDIT_CARD.id));
    }

    get carouselPaymentMethods(): Array<PaymentMethod> {
        if (!this.expressPaymentMethods.length) {
            return this.availablePaymentMethods;
        }
        // Exclude express payments methods
        return filterPaymentMethods(
            this.availablePaymentMethods,
            this.expressPaymentMethods.map((method) => method.id),
            true
        );
    }

    /*
     * Get carousel payment method's component.
     * Avoid getting payment method from other section (express),
     * if select payment method switch from carousel to express.
     */
    get carouselPaymentMethodComponent() {
        if (!this.carouselPaymentMethods.find((method) => method.id === this.selectedCarouselMethod?.id)) {
            this.selectedCarouselMethod = this.defaultMethod;
        }

        return this.getPaymentMethodComponent(this.selectedCarouselMethod?.id);
    }

    /*
     * Get payment method component,
     * used either from template or component instance
     */
    getPaymentMethodComponent(selectedCarouselMethod) {
        return getPaymentMethodComponent(selectedCarouselMethod);
    }

    get formattedAmount() {
        return formatCurrency(
            this.userInputAmount ? this.userInputAmount : '0',
            this.currencyLocale,
            '0,0.00'
        );
    }

    get totalAmount() {
        return this.selectedPaymentMethodFees.length > 0
            ? this.amount + convertAmountToBaseUnits(this.totalFeeAmount)
            : this.amount;
    }

    get formattedTotalAmount() {
        return formatCurrency(
            this.totalAmount || '0',
            this.currencyLocale,
            '0,0.00'
        );
    }

    get exchangeRate() {
        return this.getExchangeRate(this.order.currencyCode, this.countryCode);
    }

    get formattedExchangeAmount() {
        if (!this.exchangeRate) {
            return null;
        }

        return formatCurrencyRounded(
            (this.amount * this.exchangeRate.rate) || '0',
            this.currencyLocale,
            '0,0.00'
        );
    }

    get formattedTotalExchangeAmount() {
        if (!this.exchangeRate) {
            return null;
        }

        return formatCurrencyRounded(
            (this.totalAmount * this.exchangeRate.rate) || '0',
            this.currencyLocale,
            '0,0.00'
        );
    }

    get selectedPaymentMethodFees() {
        if (!this.selectedCarouselMethod) {
            return [];
        }

        const fees = getPaymentMethodFees(this.selectedCarouselMethod?.id, this.availableFees);
        // In case of Wallet fee, amount is being set to zero
        return this.selectedCarouselMethod?.id === PAYMENT_METHOD_ID.Wallet
            ? fees.map((fee) => ({ ...fee, amount: 0 }))
            : fees;
    }

    get selectedPaymentMethodFeesWithAmount() {
        /*
         In case of PAYMENT_METHOD_ID.Wallet method we don't
           want to filter fees based on amount
         */
        return this.selectedCarouselMethod?.id === PAYMENT_METHOD_ID.Wallet
            ? this.selectedPaymentMethodFees
            : this.selectedPaymentMethodFees.filter((fee) => fee.amount > 0);
    }

    get totalFeeAmount() {
        return getTotalFeeAmount(this.selectedPaymentMethodFees);
    }

    get formReadyForSubmit() {
        return (
            !this.loading &&
            !this.chargeModalOpen
        );
    }

    get otpMobile() {
        return `+${this.user?.mobile}`;
    }

    get requiresPaymentCode() {
        return this.order.serviceId === SERVICE_ID.Intrum;
    }

    updateCustomerNotes(customerNotes: string) {
        this.customerNotes = this.unescapeHtml(customerNotes) || '';
    }

    includeSurcharge(surcharge: SurchargeInfo) {
        this.availableFees = updateSurcharge(this.availableFees, surcharge);
    }

    async validateAmount() {
        return await this.amountForm?.validate();
    }

    async validateNotes() {
        return await this.notesForm?.validate();
    }

    async validatePaymentCode() {
        const [amountValid, notesValid] = await Promise.all([
            this.amountForm?.validate(),
            this.notesForm?.validate()
        ]);

        if (!amountValid || !notesValid) {
            return false;
        }

        const response = await CheckoutService.validatePaymentCode({
            merchantTrns: this.customerNotes,
            amount: convertAmountToCents(this.amount)
        });

        if (!response.success) {
            this.notesForm?.setErrors({
                [this.$t('common:paymentCode') as string]: this.$t('errors:payment_code_invalid') as string
            });

            return false;
        }

        return true;
    }

    async validateForms() {
        if (this.requiresPaymentCode) {
            return await this.validatePaymentCode();
        }

        return (!this.amountForm || await this.amountForm.validate()) &&
            (!this.notesForm || await this.notesForm.validate());
    }

    async formSubmit(chargeDetails: ChargeDetails) {
        const valid = await this.validateForms();

        if (this.formReadyForSubmit && valid) {
            this.chargeDetails = chargeDetails;
            this.selectedMethod = this.selectedCarouselMethod;
            this.chargeModalOpen = true;
        }
    }

    async chargeTokenReceived(data: TokenData) {
        const valid = await this.validateForms();

        if (this.formReadyForSubmit && valid) {
            this.tokenData = data;
            this.chargeDetails = {
                token: data.token
            };

            this.selectedMethod = findPaymentMethod(
                this.tokenData.paymentMethodId as PAYMENT_METHOD_ID
            ) as PaymentMethod;

            this.chargeModalOpen = true;
        }
    }

    async redirectToStatusPage(success: boolean, eventId: EVENT_ID | null) {
        await this.$router.push({
            name: success ? 'success' : 'fail',
            params: {
                eventId: `${eventId ?? ''}`
            }
        });
    }

    decline(eventId: EVENT_ID) {
        this.chargeModalOpen = false;
        this.isDeclineModalShown = true;
        this.eventId = eventId;
    }

    get supportsReceipt() {
        return this.transactionInfo.loyalty.programId === LoyaltyProgramId.EurobankEpistrofi;
    }

    error(message) {
        this.chargeModalOpen = false;
        this.errorModalShown = true;
        this.errorModalMessage = message;
    }

    handleLoyaltySuccess(data) {
        this.chargeModalOpen = false;
        this.loyaltySuccessModalOpened = true;
        this.transactionInfo = data;
    }

    removePaymentMethod(paymentMethodId) {
        this.merchant.paymentMethods = remove(this.merchant.paymentMethods,
            (merchantPaymentMethodId) => merchantPaymentMethodId !== paymentMethodId);

        if (this.carouselPaymentMethods.length > 0 && this.selectedCarouselMethod?.id === paymentMethodId) {
            this.selectedCarouselMethod = this.carouselPaymentMethods[0];
        }
    }

    get otpCreatedAtKey() {
        return `otpCreated-${this.user?.personId}`;
    }

    async sendOtp() {
        grecaptcha.enterprise.ready(() => {
            grecaptcha.enterprise
                .execute(process.env.VUE_APP_GOOGLE_RECAPTCHA, { action: 'checkout' })
                .then(async (token) => {
                    this.otp.status = OtpStatus.Pending;

                    const response = await AccountService.otp(token);

                    this.otp.requestTimestamp = dayjs(new Date()).valueOf();

                    if (response.success) {
                        this.otp.enabled = true;

                        Vue.$localStorage.setItem(this.otpCreatedAtKey, `${this.otp.requestTimestamp}`);
                    } else if (response.status === HttpStatus.CONFLICT) {
                        this.otp.enabled = true;

                        const otpDate = Vue.$localStorage.getItem(this.otpCreatedAtKey);

                        if (otpDate) {
                            this.otp.requestTimestamp = +otpDate;
                        }
                    } else if (response.status === HttpStatus.UNAUTHORIZED && this.selectedCarouselMethod) {
                        this.login(this.selectedCarouselMethod.id, this.getLang());
                    }

                    this.otp.status = OtpStatus.None;
                    this.otp.limitReached
                        = response.eventId === EventId.RegistrationCouldNotCreateOtpTooManyRequests;
                });
        });
    }

    async handleOtpValidation(otp: string) {
        if (!otp) {
            return;
        }

        const selectedWallet = await this.user?.wallets[0];

        const chargeDetails: ChargeDetails = {
            oneTimePassword: otp,
            walletId: selectedWallet?.walletId
        };

        this.otp.enabled = false;
        this.otp.status = OtpStatus.None;

        await this.formSubmit(chargeDetails);
    }

    handleInvalidOtp(eventId: EVENT_ID) {
        if (+eventId === EVENT_ID.SecurityValidateOtpInvalid) {
            this.chargeModalOpen = false;
            this.otp.enabled = true;
            this.otp.status = OtpStatus.InvalidOtp;
        }
    }
}
